import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaFire,
  FaStar,
  FaClock,
  FaCrown,
  FaLeaf,
  FaShoppingCart,
  FaHeart,
} from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import MobileHeader from '../components/MobileHeader';

const PWAHome = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCTS.LIST);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pwa-mode bg-gradient-to-b from-black via-[#1a120f] to-black text-cigar-cream">
      <MobileHeader title="CC Cigar" showBack={false} />
      
      <div className="page-content pb-8">
        {/* ===== Hero Banner ===== */}
        <div className="relative h-56 mb-8 rounded-3xl overflow-hidden shadow-[0_18px_45px_rgba(0,0,0,0.9)] border border-white/10 animate-[fadeIn_0.9s_ease-out]">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/cigarvid.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent flex items-end p-4 sm:p-5">
            <div className="space-y-1.5">
              <h1 className="text-2xl font-bold text-cigar-cream tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
                {t('home.title')}
              </h1>
              <p className="text-xs text-cigar-cream/80 max-w-[280px]">
                {t('pwa.about.description')}
              </p>
              <div className="flex gap-2 text-[10px] pt-1 flex-wrap">
                <Link
                  to="/products?category=Cuban"
                  className="px-2 py-1 rounded-full bg-gradient-to-r from-cigar-gold/30 to-amber-500/40 text-cigar-dark font-semibold shadow-sm border border-amber-200/60 active:scale-95 transition-all"
                >
                  Cuban
                </Link>
                <Link
                  to="/products?category=Non-Cuban"
                  className="px-2 py-1 rounded-full bg-white/8 text-cigar-cream border border-white/20 backdrop-blur-sm active:scale-95 transition-all hover:bg-white/12"
                >
                  Non‑Cuban
                </Link>
                <Link
                  to="/products?category=Cigarillo"
                  className="px-2 py-1 rounded-full bg-white/8 text-cigar-cream border border-white/20 backdrop-blur-sm active:scale-95 transition-all hover:bg-white/12"
                >
                  Cigarillos
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Quick Actions ===== */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Link
            to="/products"
            className="bg-gradient-to-br from-cigar-gold/25 via-amber-500/20 to-transparent backdrop-blur-xl border border-cigar-gold/60 rounded-2xl p-4 active:scale-95 transition-all duration-200 shadow-[0_10px_30px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 hover:border-amber-300"
          >
            <FaFire className="text-amber-400 bg-cigar-gold/40 rounded-full p-2.5 text-lg mb-2 shadow-md" />
            <h3 className="text-sm font-semibold text-cigar-cream tracking-wide">{t('home.shopNow')}</h3>
            <p className="text-xs text-cigar-cream/70">{t('pwa.categories.featured')}</p>
          </Link>
          <Link
            to="/products"
            className="bg-gradient-to-br from-cigar-bronze/25 via-orange-500/15 to-transparent backdrop-blur-xl border border-cigar-bronze/60 rounded-2xl p-4 active:scale-95 transition-all duration-200 shadow-[0_10px_30px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 hover:border-orange-300"
          >
            <FaStar className="text-cigar-bronze bg-orange-500/20 rounded-full p-2.5 text-lg mb-2 shadow-md" />
            <h3 className="text-sm font-semibold text-cigar-cream tracking-wide">{t('home.premiumQuality')}</h3>
            <p className="text-xs text-cigar-cream/70">Curated Selection</p>
          </Link>
        </div>

        {/* ===== Featured Cigars Section ===== */}
        <section className="mb-8">
          <div className="flex items-center gap-2.5 mb-4 px-1">
            <h2 className="text-lg font-bold text-cigar-cream tracking-wide">
              🔥 Featured Cigars
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {loading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : products.length === 0 ? (
              <p className="text-xs text-cigar-cream/50 col-span-2">No featured cigars available yet.</p>
            ) : (
              products.slice(0, 4).map((product) => (
                <Link
                  key={product._id}
                  to={`/products/${product._id}`}
                  className="bg-gradient-to-b from-white/10 to-white/3 border border-white/12 rounded-xl overflow-hidden shadow-md active:scale-95 transition-transform duration-200 hover:border-cigar-gold/60 hover:shadow-[0_8px_20px_rgba(0,0,0,0.7)] group"
                >
                  <div className="h-24 w-full bg-black/70 overflow-hidden">
                    <img
                      src={
                        (product.images && product.images[0]) ||
                        product.image ||
                        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop'
                      }
                      alt={product.product_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-2">
                    <h3 className="text-[10px] font-semibold text-cigar-cream line-clamp-2">
                      {product.product_name}
                    </h3>
                    <p className="text-[9px] text-cigar-cream/60 mb-1">
                      {product.brand || product.category}
                    </p>
                    {product.price_single > 0 && (
                      <p className="text-xs font-bold text-cigar-gold">
                        {product.currency} {product.price_single}
                      </p>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </section>

        {/* ===== About App Section ===== */}
        <section className="mb-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-4">
          <h2 className="text-base font-bold text-cigar-cream mb-2 tracking-wide">
            ℹ️ {t('pwa.about.title')}
          </h2>
          <p className="text-xs text-cigar-cream/80 mb-3 leading-relaxed">
            {t('pwa.about.mission')}
          </p>
          <div className="flex gap-2 text-[11px]">
            <Link
              to="/products"
              className="flex-1 bg-cigar-gold/20 border border-cigar-gold/40 text-cigar-cream rounded-lg py-2 px-3 text-center font-semibold hover:bg-cigar-gold/30 transition-colors"
            >
              Shop Now →
            </Link>
            <button className="flex-1 bg-white/8 border border-white/20 text-cigar-cream rounded-lg py-2 px-3 text-center font-semibold hover:bg-white/12 transition-colors">
              Learn More
            </button>
          </div>
        </section>

        {/* ===== Category Sections ===== */}
        <div className="space-y-6">
          {/* Cuban Cigars */}
          <CategorySection
            title="Cuban Classics"
            subtitle="Iconic marcas from Havana"
            icon={<FaCrown className="text-cigar-gold" />}
            products={products.filter((p) => p.category === 'Cuban').slice(0, 10)}
            loading={loading}
            accentColor="from-cigar-gold/30 to-transparent"
          />

          {/* Non-Cuban Cigars */}
          <CategorySection
            title="Non‑Cuban Gems"
            subtitle="Boutique blends & modern favorites"
            icon={<FaLeaf className="text-cigar-bronze" />}
            products={products.filter((p) => p.category === 'Non-Cuban').slice(0, 10)}
            loading={loading}
            accentColor="from-cigar-bronze/30 to-transparent"
          />

          {/* Cigarillos */}
          <CategorySection
            title="Cigarillos"
            subtitle="Short quality smokes"
            icon={<FaClock className="text-amber-300" />}
            products={products.filter((p) => p.category === 'Cigarillo').slice(0, 10)}
            loading={loading}
            accentColor="from-amber-300/30 to-transparent"
          />
        </div>

        {/* ===== View All CTA ===== */}
        <div className="mt-8 rounded-2xl border border-white/15 bg-gradient-to-r from-cigar-dark/50 to-black/30 backdrop-blur p-5 text-center">
          <h3 className="text-sm font-bold text-cigar-cream mb-3">
            🔥 Browse Full Collection
          </h3>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cigar-gold to-amber-500 text-cigar-dark px-6 py-2.5 rounded-xl font-semibold hover:shadow-[0_10px_25px_rgba(212,175,55,0.3)] transition-all hover:-translate-y-0.5"
          >
            Shop All Products <FaArrowRight className="text-sm" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PWAHome;

/* ===== Category Section Component ===== */
const CategorySection = ({ title, subtitle, icon, products, loading, accentColor }) => {
  return (
    <div className="animate-[fadeIn_0.6s_ease-out]">
      <div className={`bg-gradient-to-r ${accentColor} backdrop-blur border border-white/10 rounded-2xl p-3 mb-3.5`}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shadow-sm border border-white/15 text-base">
            {icon}
          </div>
          <div>
            <h2 className="text-sm font-bold text-cigar-cream tracking-wide">
              {title}
            </h2>
            <p className="text-[11px] text-cigar-cream/60">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto hide-scrollbar">
        <div className="flex gap-3.5 pb-1.5">
          {loading && (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          )}
          {!loading && products && products.length === 0 && (
            <p className="text-xs text-cigar-cream/50 px-1">
              No cigars in this category yet.
            </p>
          )}
          {!loading &&
            products &&
            products.map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
        </div>
      </div>
    </div>
  );
};

/* ===== Product Card Component ===== */
const ProductCard = ({ product }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);

  const handleWishlist = (e) => {
    e.preventDefault();
    setIsWishlisted(!isWishlisted);
  };

  return (
    <Link
      to={`/products/${product._id}`}
      className="w-40 flex-shrink-0 bg-gradient-to-b from-white/10 to-white/3 border border-white/12 rounded-2xl overflow-hidden shadow-md active:scale-95 transition-transform duration-200 hover:border-cigar-gold/60 hover:shadow-[0_12px_30px_rgba(0,0,0,0.7)] group relative"
    >
      {/* Wishlist Button */}
      <button
        onClick={handleWishlist}
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/50 backdrop-blur flex items-center justify-center z-10 hover:bg-black/70 transition-all"
      >
        <FaHeart
          className={`text-xs ${isWishlisted ? 'text-red-500' : 'text-white/60'}`}
        />
      </button>

      {/* Image */}
      <div className="h-32 w-full bg-black/70 relative overflow-hidden">
        <img
          src={
            (product.images && product.images[0]) ||
            product.image ||
            'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop'
          }
          alt={product.product_name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
      </div>

      {/* Info */}
      <div className="p-2">
        <h3 className="text-[11px] font-semibold text-cigar-cream line-clamp-2 min-h-[30px]">
          {product.product_name}
        </h3>
        <p className="text-[10px] text-cigar-cream/60 mb-1 line-clamp-1">
          {product.brand || product.category}
        </p>
        {product.price_single > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-cigar-gold drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">
              {product.currency} {product.price_single}
            </p>
            <FaShoppingCart className="text-xs text-cigar-gold/70" />
          </div>
        )}
      </div>
    </Link>
  );
};

/* ===== Skeleton Loading Card ===== */
const SkeletonCard = () => (
  <div className="w-40 flex-shrink-0 rounded-2xl overflow-hidden bg-white/5 border border-white/8 animate-pulse">
    <div className="h-32 w-full bg-gradient-to-br from-white/10 to-white/5" />
    <div className="p-2 space-y-2">
      <div className="h-3 bg-white/12 rounded" />
      <div className="h-3 bg-white/7 rounded w-2/3" />
      <div className="h-3 bg-cigar-gold/30 rounded w-1/2" />
    </div>
  </div>
);
